import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useInstructionDictation } from '@/hooks/useInstructionDictation';
import { ArrowUp, Loader2, Sparkles } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { DictationButton } from './DictationButton';
import { SuggestionChips } from './SuggestionChips';
import {
  composerCaptionClasses,
  composerErrorClasses,
  composerReplyClasses,
  composerShellClasses,
  composerTextareaClasses,
} from './noteInstructions.styles';
import type { InstructionComposerProps } from './noteInstructions.types';

const PLACEHOLDER = 'Biddu Skríbuna um að breyta nótunni eða verkefnunum…';
const LISTENING_PLACEHOLDER = 'Hlusta… segðu Skríbunni hvað á að gera';

export const InstructionComposer = ({
  suggestions,
  isPending,
  disabled,
  notice,
  error,
  onSubmit,
}: InstructionComposerProps) => {
  const inputId = useId();
  const [value, setValue] = useState('');
  const isLocked = isPending || !!disabled;
  const dictation = useInstructionDictation({
    // Spoken instructions apply as heard. If the note is busy (generating, unsaved edits), keep the text to send later.
    onInstruction: (text) => {
      if (!isLocked) return onSubmit(text);
      setValue(text);
      return Promise.resolve(false);
    },
  });
  const isDictating = dictation.status !== 'idle';
  const shownError = error ?? dictation.error;
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // A long spoken instruction outgrows the input; keep the newest words in view.
  const liveText = dictation.liveText;
  useEffect(() => {
    const input = inputRef.current;
    if (input && isDictating && liveText) input.scrollTop = input.scrollHeight;
  }, [isDictating, liveText]);

  const send = async (instruction: string) => {
    const trimmed = instruction.trim();
    if (!trimmed || isLocked || isDictating) return;
    dictation.clearError();
    setValue(trimmed);
    if (await onSubmit(trimmed)) setValue('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send(value);
    }
  };

  return (
    <div data-testid="instruction-composer">
      {!value && !isPending && !isDictating && (
        <SuggestionChips suggestions={suggestions} disabled={isLocked} onSelect={(text) => void send(text)} />
      )}
      <div className={composerShellClasses}>
        <Sparkles className="mb-3 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <label htmlFor={inputId} className="sr-only">
          Fyrirmæli til Skríbunnar
        </label>
        <Textarea
          ref={inputRef}
          id={inputId}
          rows={1}
          value={isDictating ? dictation.liveText : value}
          onChange={(event) => {
            setValue(event.target.value);
            if (dictation.error) dictation.clearError();
          }}
          onKeyDown={handleKeyDown}
          placeholder={dictation.status === 'listening' ? LISTENING_PLACEHOLDER : PLACEHOLDER}
          readOnly={isDictating}
          disabled={isLocked}
          maxLength={2000}
          className={composerTextareaClasses}
        />
        <DictationButton
          status={dictation.status}
          disabled={isLocked || dictation.isBlocked}
          onStart={dictation.start}
          onStop={dictation.stop}
        />
        <Button
          type="button"
          size="icon"
          className="h-9 w-9 rounded-xl"
          disabled={isLocked || isDictating || !value.trim()}
          onClick={() => void send(value)}
          aria-label="Senda fyrirmæli"
        >
          {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
        </Button>
      </div>
      {/* An error always wins, so a notice (e.g. the model's reply) never contradicts it */}
      {notice && !isPending && !shownError && (
        <output aria-live="polite" className={composerReplyClasses}>
          {notice}
        </output>
      )}
      {shownError && (
        <p role="alert" className={composerErrorClasses}>
          {shownError}
        </p>
      )}
      <p className={composerCaptionClasses}>
        {isPending
          ? 'Skríban vinnur úr fyrirmælunum…'
          : isDictating
            ? 'Skríban framkvæmir fyrirmælin þegar þú gerir hlé.'
            : 'Farðu yfir nótuna áður en hún er notuð — hún á að endurspegla viðtalið rétt.'}
      </p>
    </div>
  );
};
